/**
 * Cache-key separation for pages whose SSR output branches on auth.
 *
 * These routes render a different grid for subscribers (no free/premium
 * interspersing) and embed `__auth` in their props, so a logged-in visitor
 * must never be handed the cached anonymous copy. That used to be enforced
 * with `Vary: x-auth-state` — but Vercel's CDN refuses to cache *any* response
 * carrying a custom `Vary`, so those routes returned `x-vercel-cache: MISS` on
 * every single request and ran their Typesense prefetch every time.
 *
 * Instead, middleware appends `_auth=1` to logged-in requests. Query params are
 * part of the CDN cache key, so the two variants can no longer collide, and the
 * anonymous copy caches normally. This mirrors the `_page_type` marker the
 * collection rewrites already use.
 *
 * The logged-in variant still sets `no-store`, so the marked key is never
 * stored — the param exists purely to keep it away from the anonymous entry.
 */
export const AUTH_VARIANT_PARAM = '_auth';

/**
 * Roots whose whole subtree varies by auth — listings, pagination and
 * categories all branch on the free/premium split.
 */
const AUTH_VARIANT_ROOTS = ['/motion-graphics', '/luts'];

/** Auth-varying subtrees under a root that is otherwise auth-agnostic. */
const AUTH_VARIANT_SUBTREES = [
  '/stock-video/category',
  '/stock-video/explore',
  '/music/category',
  '/sfx/category',
  '/music/collection',
  '/sfx/collection',
];

/**
 * Subtrees where only the child paths vary by auth. `/contributors` itself is
 * a static listing with no auth branching, so splitting it would buy nothing;
 * `/contributors/[slug]` reads `getServerSideSubscriptionType` to pick its
 * Typesense query, so its dehydrated grid differs by subscription.
 */
const AUTH_VARIANT_CHILDREN_ONLY = ['/contributors'];

/** Exact paths that vary by auth but whose subtree does not. */
const AUTH_VARIANT_EXACT = ['/stock-video'];

const isAtOrUnder = (pathname: string, base: string) =>
  pathname === base || pathname.startsWith(`${base}/`);

/**
 * Whether `pathname` renders an auth-dependent variant and therefore needs a
 * separate CDN cache key when the visitor is logged in.
 *
 * Asset pages are excluded deliberately: they read no auth at all, so one
 * cached copy serves everyone and splitting them would only halve their hit
 * rate.
 */
export function isAuthVariantPath(pathname: string): boolean {
  if (pathname.includes('/asset/')) {
    return false;
  }

  if (AUTH_VARIANT_EXACT.includes(pathname)) {
    return true;
  }

  if (
    AUTH_VARIANT_CHILDREN_ONLY.some((base) => pathname.startsWith(`${base}/`))
  ) {
    return true;
  }

  return [...AUTH_VARIANT_ROOTS, ...AUTH_VARIANT_SUBTREES].some((base) =>
    isAtOrUnder(pathname, base)
  );
}
